import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ConfirmDialogService } from './confirm-dialog.service';
import type {
  ConfirmVariant,
  DetalheVisual,
  PromptOptions,
} from './confirm-dialog.types';

/**
 * Host do ConfirmDialog — montado uma vez no app shell. Lê
 * `service.dialog()` e renderiza modal animado quando há um.
 *
 * Comportamento:
 * - Backdrop click → resolve(false/null) (cancelar).
 * - ESC global → cancelar.
 * - Confirmar → resolve(true/string).
 * - Prompt: valida minLength antes de aceitar.
 */
@Component({
  selector: 'app-confirm-dialog-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
  template: `
    @if (service.dialog(); as d) {
      <div
        class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-dialog-in"
        role="presentation"
        (click)="onBackdrop($event)"
      >
        <div
          role="dialog"
          aria-modal="true"
          [attr.aria-labelledby]="'confirm-dialog-title'"
          class="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl shadow-2xl bg-white dark:bg-neutral-900 p-6 m-0 sm:m-4 animate-dialog-card-in"
          [class.animate-shake-x]="shouldShake(d.options.variant)"
          (click)="$event.stopPropagation()"
        >
          @if (d.options.icon) {
            <div
              class="text-5xl text-center mb-3"
              aria-hidden="true"
              [class.animate-icon-pulse]="d.options.variant === 'vote'"
              [class.animate-icon-bounce]="d.options.variant === 'purchase'"
              [class.animate-icon-flame]="d.options.variant === 'sink'"
            >
              {{ d.options.icon }}
            </div>
          }

          <h2
            id="confirm-dialog-title"
            class="text-lg font-bold text-center text-neutral-900 dark:text-neutral-100 mb-2"
          >
            {{ d.options.titulo }}
          </h2>

          @if (d.options.descricao) {
            <p class="text-sm text-center text-neutral-600 dark:text-neutral-400 mb-4">
              {{ d.options.descricao }}
            </p>
          }

          @if (d.options.detalhe; as det) {
            <div
              [class]="detalheBoxClasses()"
            >
              <span class="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                {{ det.label }}
              </span>
              <span class="text-base font-bold tabular-nums text-neutral-900 dark:text-neutral-100">
                {{ det.valor }}
              </span>
            </div>
          }

          @if (d.kind === 'prompt'; as _) {
            <input
              type="text"
              [(ngModel)]="inputValue"
              (keydown.enter)="onConfirm()"
              [placeholder]="promptPlaceholder()"
              [maxlength]="promptMaxLength()"
              class="w-full px-3 py-2 rounded-lg border bg-white dark:bg-neutral-800 mb-2 focus:outline-none focus:ring-2 focus:ring-brand-500"
              [class.border-red-400]="inputErro()"
              [class.dark:border-red-600]="inputErro()"
              [class.border-neutral-300]="!inputErro()"
              [class.dark:border-neutral-700]="!inputErro()"
            />
            @if (inputErro()) {
              <p class="text-xs text-red-600 dark:text-red-400 mb-2">
                {{ inputErro() }}
              </p>
            }
          }

          <div class="flex gap-2 mt-2">
            <button
              type="button"
              (click)="onCancel()"
              class="flex-1 px-4 py-3 rounded-lg border border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold min-h-[44px] hover:bg-neutral-50 dark:hover:bg-neutral-800"
            >
              {{ d.options.botaoCancelar ?? 'Cancelar' }}
            </button>
            <button
              type="button"
              (click)="onConfirm()"
              class="flex-1 px-4 py-3 rounded-lg text-white font-semibold min-h-[44px]"
              [class]="confirmButtonClasses()"
            >
              {{ d.options.botaoConfirmar ?? 'Confirmar' }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
})
export class ConfirmDialogHostComponent {
  readonly service = inject(ConfirmDialogService);

  readonly inputValue = signal('');
  readonly inputTouched = signal(false);

  private readonly currentPrompt = computed<PromptOptions | null>(() => {
    const d = this.service.dialog();
    if (!d || d.kind !== 'prompt') return null;
    return d.options as PromptOptions;
  });

  readonly promptPlaceholder = computed(
    () => this.currentPrompt()?.placeholder ?? '',
  );
  readonly promptMaxLength = computed(
    () => this.currentPrompt()?.maxLength ?? 255,
  );

  readonly inputErro = computed(() => {
    if (!this.inputTouched()) return null;
    const p = this.currentPrompt();
    if (!p) return null;
    const v = this.inputValue().trim();
    const min = p.minLength ?? 0;
    if (min > 0 && v.length < min) {
      return `Mínimo ${min} caracteres.`;
    }
    return null;
  });

  readonly detalheBoxClasses = computed(() => {
    const base = 'rounded-lg p-3 mb-4 flex items-center justify-between gap-3';
    const v = this.service.dialog()?.options.variant;
    if (v === 'sink') return `${base} bg-orange-50 dark:bg-orange-950/30`;
    if (v === 'purchase') return `${base} bg-emerald-50 dark:bg-emerald-950/30`;
    return `${base} bg-neutral-50 dark:bg-neutral-800`;
  });

  readonly confirmButtonClasses = computed(() => {
    const v = this.service.dialog()?.options.variant;
    switch (v) {
      case 'sink':
        return 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600';
      case 'purchase':
        return 'bg-emerald-500 hover:bg-emerald-600';
      case 'vote':
        return 'bg-orange-500 hover:bg-orange-600';
      case 'destructive':
      case 'destructive-prompt':
        return 'bg-red-500 hover:bg-red-600';
      case 'info':
      default:
        return 'bg-brand-500 hover:bg-brand-600';
    }
  });

  shouldShake(variant: ConfirmVariant): boolean {
    return variant === 'destructive' || variant === 'destructive-prompt';
  }

  onBackdrop(_e: MouseEvent): void {
    this.cancel();
  }

  @HostListener('window:keydown.escape')
  onEsc(): void {
    if (this.service.dialog()) this.cancel();
  }

  onCancel(): void {
    this.cancel();
  }

  onConfirm(): void {
    const d = this.service.dialog();
    if (!d) return;
    if (d.kind === 'prompt') {
      this.inputTouched.set(true);
      const v = this.inputValue().trim();
      const p = d.options as PromptOptions;
      const min = p.minLength ?? 0;
      if (min > 0 && v.length < min) {
        return;
      }
      this.service.resolve(v);
      this.reset();
      return;
    }
    this.service.resolve(true);
  }

  private cancel(): void {
    const d = this.service.dialog();
    if (!d) return;
    if (d.kind === 'prompt') {
      this.service.resolve(null);
      this.reset();
      return;
    }
    this.service.resolve(false);
  }

  private reset(): void {
    this.inputValue.set('');
    this.inputTouched.set(false);
  }

  /** `det` typed helper pra evitar erros de strict-template (não usado). */
  protected typedDetalhe(d: DetalheVisual): DetalheVisual {
    return d;
  }
}
